import React, { Component } from 'react';

import {
  ConceptSelector,
  FlagDropdown,
  TextEditor,
  parseCues,
} from '../../index';

import { ALPHA_FLAG } from '../../utils/constants';

// Default instructions that appear when user types '/'
const DEFAULT_FILL_IN_BLANKS_INSTRUCTIONS = [
  'Fill in the blank with the correct joining word.',
  'Fill in the blank with the correct word.',
  'Fill in the blank with the correct pronoun.',
  'Fill in the blank with the correct action word.',
  'Fill in the blank with the correct set of words.',
  'Fill in the blank with the correct option.'
];

interface FillInBlankFormState {
  blankAllowed: boolean;
  blankByBlankGrading?: any;
  caseInsensitive: boolean;
  conceptID: string;
  cues: string;
  cuesLabel: string;
  flag: string;
  instructions: string;
  newQuestionOptimalResponse: string;
  prompt: string;
  ruleDescription: string;
  showDefaultInstructions: boolean;
  attemptedSaveWithMissingConcept?: boolean;
}

interface FillInBlankFormProps {
  action: (data: any, optimalResponse?: any) => void;
  editing?: boolean;
  isGrammar?: boolean;
  new?: boolean;
  showOptimalResponse?: boolean;
  state?: Partial<FillInBlankFormState>;
}

const DEFAULT_STATE = {
  prompt: '',
  blankAllowed: false,
  caseInsensitive: true,
  instructions: '',
  cues: '',
  conceptID: '',
  flag: ALPHA_FLAG,
  cuesLabel: '',
  newQuestionOptimalResponse: '',
  ruleDescription: '',
  showDefaultInstructions: false
};


export class FillInBlankForm extends Component<FillInBlankFormProps, FillInBlankFormState> {
  constructor(props: FillInBlankFormProps) {
    super(props);

    // Initialize with default values, then merge in any provided state
    this.state = {
      ...DEFAULT_STATE,
      ...(props.state || {})
    };
  }

  clearForm = () => {
    this.setState(DEFAULT_STATE);
  }

  handlePromptChange = (prompt: string) => {
    this.setState({ prompt });
  };

  handleInstructionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    this.setState({ instructions: value });

    // Show default instructions dropdown if user types '/' (Connect feature)
    if (value === '/') {
      this.setState({ showDefaultInstructions: true });
    } else {
      this.setState({ showDefaultInstructions: false });
    }
  };

  handleCuesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ cues: e.target.value });
  };

  handleCuesLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ cuesLabel: e.target.value });
  };

  handleNewQuestionOptimalResponseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ newQuestionOptimalResponse: e.target.value });
  };

  handleSelectorChange = (e: { value: string }) => {
    this.setState({ conceptID: e.value });
  };

  handleFlagChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({ flag: e.target.value });
  };

  toggleQuestionBlankAllowed = () => {
    this.setState(prevState => ({ blankAllowed: !prevState.blankAllowed }));
  };

  toggleQuestionCaseInsensitive = () => {
    this.setState(prevState => ({ caseInsensitive: !prevState.caseInsensitive }));
  };

  handleRuleDescriptionChange = (ruleDescription: string) => {
    this.setState({ ruleDescription });
  };

  renderDefaultInstructions = () => {
    const { showDefaultInstructions } = this.state;

    if (!showDefaultInstructions) {
      return null;
    }

    const defaultInstructionsButtons = DEFAULT_FILL_IN_BLANKS_INSTRUCTIONS.map((item, i) => (
      <button
        className="default"
        key={i}
        onClick={this.handleInstructionsChange as any}
        type="button"
        value={item}
      >
        {item}
      </button>
    ));

    return (
      <div style={{ position: 'relative' }}>
        <div className='default-instructions'>
          {defaultInstructionsButtons}
        </div>
      </div>
    );
  };

  submit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    const { action, new: isNew, state } = this.props;
    const {
      blankAllowed,
      caseInsensitive,
      conceptID,
      cues,
      cuesLabel,
      flag,
      instructions,
      newQuestionOptimalResponse,
      prompt,
      ruleDescription
    } = this.state;

    if (!conceptID || conceptID === '') {
      this.setState({ attemptedSaveWithMissingConcept: true })
      return
    }

    const data = {
      prompt,
      blankAllowed,
      caseInsensitive,
      cues: parseCues(cues),
      instructions,
      conceptID,
      flag,
      cuesLabel,
      ruleDescription,
      // Preserve blankByBlankGrading from original question data
      ...(state?.blankByBlankGrading && { blankByBlankGrading: state.blankByBlankGrading })
    };

    // Special handling for new questions (Connect feature)
    if (isNew && data.prompt !== '') {
      action(
        data,
        {
          text: newQuestionOptimalResponse.trim(),
          optimal: true,
          count: 0,
          feedback: 'That\'s a strong sentence!',
          concept_results: [{ conceptUID: conceptID, correct: true }]
        }
      );
    } else {
      action(data, newQuestionOptimalResponse);
    }
  };

  renderRuleDescription = () => {
    const { isGrammar } = this.props;

    if (!isGrammar) {
      return null;
    }

    const { ruleDescription } = this.state;

    return (
      <React.Fragment>
        <label className="label" htmlFor="fill-in-blank-rule-description">
          Rule description (optional, will overwrite the concept's description for this question if set)
        </label>
        <p className="control">
          <TextEditor
            handleTextChange={this.handleRuleDescriptionChange}
            id="fill-in-blank-rule-description"
            text={ruleDescription}
          />
        </p>
      </React.Fragment>
    );
  }

  renderOptimalField = () => {
    const { editing, showOptimalResponse = true } = this.props;
    const { newQuestionOptimalResponse } = this.state;

    if (editing || !showOptimalResponse) {
      return null;
    }

    return (
      <div>
        <label className="label" htmlFor="fill-in-blank-optimal-response">
          Optimal Response
          <p className="control">
            <input
              className="input"
              id="fill-in-blank-optimal-response"
              onChange={this.handleNewQuestionOptimalResponseChange}
              type="text"
              value={newQuestionOptimalResponse}
            />
          </p>
        </label>
      </div>
    );
  }

  renderButtonText = () => {
    const { editing } = this.props;

    return editing ? 'Submit Edit' : 'Add Question';
  }

  render() {
    const { editing, } = this.props
    const {
      blankAllowed,
      caseInsensitive,
      conceptID,
      cues,
      cuesLabel,
      flag,
      instructions,
      prompt,
      attemptedSaveWithMissingConcept,
    } = this.state;

    return (
      <form className="box" onSubmit={this.submit}>
        <h6 className="control subtitle">{editing ? 'Edit Question' : 'Create a new question'}</h6>

        <label className="label" htmlFor="fill-in-blank-prompt">Prompt</label>
        <TextEditor
          handleTextChange={this.handlePromptChange}
          text={prompt}
        />
        <br />

        <label className="label" htmlFor="fill-in-blank-instructions">
          Instructions for student
          <p className="control">
            <textarea
              className="input"
              id="fill-in-blank-instructions"
              onChange={this.handleInstructionsChange}
              placeholder="Type '/' for list of instructions"
              type="text"
              value={instructions}
            />
          </p>
        </label>
        {this.renderDefaultInstructions()}

        <label className="label" htmlFor="fill-in-blank-cues-label">
          Cues Label (default is &quot;joining words&quot;/&quot;joining word&quot; for single cues, enter a space to have no label)
          <p className="control">
            <input
              className="input"
              id="fill-in-blank-cues-label"
              onChange={this.handleCuesLabelChange}
              type="text"
              value={cuesLabel}
            />
          </p>
        </label>

        <label className="label" htmlFor="fill-in-blank-cues">
          Cues (separated by commas, no spaces eg &quot;however,therefore,hence&quot;. To include a literal comma in a cue, escape it as &quot;\,&quot;)
          <p className="control">
            <input
              className="input"
              id="fill-in-blank-cues"
              onChange={this.handleCuesChange}
              type="text"
              value={cues}
            />
          </p>
        </label>

        {this.renderRuleDescription()}

        {this.renderOptimalField()}

        <label className="label" htmlFor="fill-in-blank-blank-allowed">
          Empty blank allowed?
          <p className="control">
            <input
              checked={blankAllowed}
              id="fill-in-blank-blank-allowed"
              onChange={this.toggleQuestionBlankAllowed}
              type="checkbox"
            />
          </p>
        </label>

        <label className="label" htmlFor="fill-in-blank-case-insensitive">
          Case Insensitive?
          <p className="control">
            <input
              checked={caseInsensitive}
              id="fill-in-blank-case-insensitive"
              onChange={this.toggleQuestionCaseInsensitive}
              type="checkbox"
            />
          </p>
        </label>

        <FlagDropdown
          flag={flag}
          handleFlagChange={this.handleFlagChange}
          isLessons={false}
        />

        <label className={attemptedSaveWithMissingConcept ? 'red label' : 'label'} htmlFor="fill-in-blank-concept">Concept</label>
        <ConceptSelector
          currentConceptUID={conceptID}
          handleSelectorChange={this.handleSelectorChange}
        />
        {attemptedSaveWithMissingConcept && <span className="red missing-concept">Add a concept to save this question.</span>}
        <br />

        <button
          className="quill-button contained medium green focus-on-light"
          type="submit"
        >
          {this.renderButtonText()}
        </button>
      </form>
    );
  }
}
